import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import axiosInstance from '../../../../axios/axiosInstance';
import apiEndpoints from '../../../../axios/api';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { localDateString } from '../../../../utils/helpers/localDate';
import ReportShell, { ReportActionButtons } from '../components/ReportShell';
import { GetReportButton } from '../components/ReportFilters';
import { getCurrentFY, formatCurrency } from '../components/helpers';

// Month-wise Income & Expenditure over any range of up to 24 months - across
// financial years if needed. Income and expenditure accounts grouped under
// their heads, per-month totals, surplus / (deficit) and a running cumulative.

const MAX_MONTHS = 24;
const labelClass = 'tw-block tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-1';
const inputClass = 'tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500';

const monthsBetween = (from, to) => {
    const [fy, fm] = from.split('-').map(Number);
    const [ty, tm] = to.split('-').map(Number);
    return (ty - fy) * 12 + (tm - fm) + 1;
};

const PRESETS = () => {
    const fy = getCurrentFY();
    const [a, b] = fy.split('-').map(Number);
    const thisMonth = localDateString().slice(0, 7);
    const shift = (m, d) => {
        const [y, mo] = m.split('-').map(Number);
        const dt = new Date(y, mo - 1 + d, 1);
        return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
    };
    return [
        { label: 'This FY to date', from: `${a}-04`, to: thisMonth },
        { label: 'Full FY', from: `${a}-04`, to: `${b}-03` },
        { label: 'Previous FY', from: `${a - 1}-04`, to: `${a}-03` },
        { label: 'Last 12 months', from: shift(thisMonth, -11), to: thisMonth },
        { label: 'Calendar year', from: `${thisMonth.slice(0, 4)}-01`, to: `${thisMonth.slice(0, 4)}-12` },
    ];
};

const Amount = ({ value, bold, signed }) => {
    const v = parseFloat(value) || 0;
    const cls = signed ? (v < 0 ? 'tw-text-rose-600' : v > 0 ? 'tw-text-emerald-700' : 'tw-text-gray-400') : (v === 0 ? 'tw-text-gray-300' : 'tw-text-gray-700');
    return (
        <td className={`tw-px-3 tw-py-1.5 tw-text-right tw-whitespace-nowrap ${bold ? 'tw-font-semibold' : ''} ${cls}`}>
            {v === 0 && !signed ? '-' : signed && v < 0 ? `(${formatCurrency(-v)})` : formatCurrency(v)}
        </td>
    );
};

const MonthlyIncomeExpenditure = () => {
    const societyId = selectedSocietyStorer.getSocietyId();
    const presets = PRESETS();
    const [fromMonth, setFromMonth] = useState(presets[0].from);
    const [toMonth, setToMonth] = useState(presets[0].to);
    const [showAccounts, setShowAccounts] = useState(true);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const span = fromMonth && toMonth ? monthsBetween(fromMonth, toMonth) : 0;
    const rangeError = !fromMonth || !toMonth ? 'Pick both months'
        : span < 1 ? '"To" month must be the same as or after "From"'
            : span > MAX_MONTHS ? `Pick at most ${MAX_MONTHS} months` : '';

    const fetchReport = useCallback(async (from = fromMonth, to = toMonth) => {
        if (!societyId || !from || !to) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(
                apiEndpoints.GET_MONTHLY_INCOME_EXPENDITURE_REPORT_API.replace(':societyId', societyId),
                { params: { fromMonth: from, toMonth: to } }
            );
            setData(res?.data || null);
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to load report');
        } finally {
            setLoading(false);
        }
    }, [societyId, fromMonth, toMonth]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchReport(); }, [societyId]);
    usePageRefresh(() => fetchReport());

    const handleGet = () => {
        if (rangeError) return toast.warn(rangeError);
        fetchReport();
    };
    const applyPreset = (p) => {
        setFromMonth(p.from);
        setToMonth(p.to);
        fetchReport(p.from, p.to);
    };

    const months = data?.months || [];

    const handleExcel = () => {
        if (!data) return;
        const header = ['Particulars', ...months.map(m => m.label), 'Total'];
        const rows = [
            [`${data.reportInfo?.societyTitle || ''} - Month-wise Income & Expenditure`],
            [`${months[0]?.label || ''} to ${months[months.length - 1]?.label || ''}`],
            [],
            header,
        ];
        const pushSide = (title, side) => {
            rows.push([title]);
            (side?.groups || []).forEach(g => {
                rows.push([g.name, ...months.map(m => g.amounts[m.key] || 0), g.total]);
                if (showAccounts) g.accounts.forEach(a => rows.push([`    ${a.name}`, ...months.map(m => a.amounts[m.key] || 0), a.total]));
            });
            rows.push([`Total ${title}`, ...months.map(m => side?.totals?.[m.key] || 0), side?.total || 0]);
            rows.push([]);
        };
        pushSide('Income', data.income);
        pushSide('Expenditure', data.expenditure);
        rows.push(['Surplus / (Deficit)', ...months.map(m => data.surplus[m.key] || 0), data.totals.surplus]);
        rows.push(['Cumulative', ...months.map(m => data.cumulative[m.key] || 0), '']);
        const ws = XLSX.utils.aoa_to_sheet(rows);
        ws['!cols'] = [{ wch: 36 }, ...months.map(() => ({ wch: 13 })), { wch: 14 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Income & Expenditure');
        XLSX.writeFile(wb, `Income_Expenditure_${fromMonth}_to_${toMonth}.xlsx`);
    };

    const sideRows = (title, side, color) => (
        <>
            <tr className={`${color} tw-border-y tw-border-gray-200`}>
                <td colSpan={months.length + 2} className="tw-px-3 tw-py-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-sticky tw-left-0">{title}</td>
            </tr>
            {(side?.groups || []).length === 0 && (
                <tr><td colSpan={months.length + 2} className="tw-px-3 tw-py-2 tw-text-gray-400 tw-italic">No {title.toLowerCase()} in this period</td></tr>
            )}
            {(side?.groups || []).map(g => (
                <FragmentGroup key={g.code} group={g} months={months} showAccounts={showAccounts} />
            ))}
            <tr className="tw-bg-gray-50 tw-border-t tw-border-gray-200">
                <td className="tw-px-3 tw-py-2 tw-font-bold tw-text-gray-800 tw-sticky tw-left-0 tw-bg-gray-50">Total {title}</td>
                {months.map(m => <Amount key={m.key} value={side?.totals?.[m.key]} bold />)}
                <Amount value={side?.total} bold />
            </tr>
        </>
    );

    return (
        <ReportShell
            title="Month-wise Income & Expenditure"
            subtitle="Any range of up to 24 months, across financial years"
            actions={data && <ReportActionButtons onExcel={handleExcel} onPrint={() => window.print()} />}
            filters={(
                <div className="tw-space-y-3">
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3 tw-items-end">
                        <div>
                            <label className={labelClass}>From Month</label>
                            <input type="month" value={fromMonth} onChange={e => setFromMonth(e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>To Month</label>
                            <input type="month" value={toMonth} onChange={e => setToMonth(e.target.value)} className={inputClass} />
                        </div>
                        <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-600 tw-pb-1.5">
                            <input type="checkbox" checked={showAccounts} onChange={e => setShowAccounts(e.target.checked)} />
                            Show individual accounts
                        </label>
                        <GetReportButton onClick={handleGet} loading={loading} />
                    </div>
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                        {presets.map(p => (
                            <button
                                key={p.label}
                                type="button"
                                onClick={() => applyPreset(p)}
                                className={`tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[11px] tw-border ${p.from === fromMonth && p.to === toMonth ? 'tw-bg-rose-600 tw-border-rose-600 tw-text-white' : 'tw-bg-white tw-border-gray-200 tw-text-gray-600 hover:tw-bg-gray-50'}`}
                            >
                                {p.label}
                            </button>
                        ))}
                        {rangeError && <span className="tw-text-[11px] tw-text-rose-600 tw-ml-2">{rangeError}</span>}
                    </div>
                </div>
            )}
        >
            {loading && !data ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-py-16">
                    <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                </div>
            ) : !data ? (
                <p className="tw-text-center tw-text-xs tw-text-gray-400 tw-py-16">Pick a month range and click Get Report.</p>
            ) : (
                <>
                    {/* Headline */}
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3 tw-p-4">
                        {[
                            { label: 'Total Income', value: data.totals.income, cls: 'tw-text-emerald-700', icon: 'mdi:arrow-down-bold-circle-outline' },
                            { label: 'Total Expenditure', value: data.totals.expenditure, cls: 'tw-text-rose-600', icon: 'mdi:arrow-up-bold-circle-outline' },
                            { label: data.totals.surplus >= 0 ? 'Surplus' : 'Deficit', value: Math.abs(data.totals.surplus), cls: data.totals.surplus >= 0 ? 'tw-text-emerald-700' : 'tw-text-rose-600', icon: 'mdi:scale-balance' },
                        ].map(c => (
                            <div key={c.label} className="tw-border tw-border-gray-100 tw-rounded-lg tw-px-3 tw-py-2.5 tw-flex tw-items-center tw-gap-2.5">
                                <Icon icon={c.icon} className={`tw-text-2xl ${c.cls}`} />
                                <div>
                                    <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide">{c.label}</p>
                                    <p className={`tw-text-sm tw-font-bold ${c.cls}`}>₹ {formatCurrency(c.value)}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="tw-overflow-x-auto tw-border-t tw-border-gray-100">
                        <table className="tw-w-full tw-text-xs">
                            <thead>
                                <tr className="tw-bg-slate-50 tw-border-b tw-border-gray-200">
                                    <th className="tw-px-3 tw-py-2.5 tw-text-left tw-text-[10px] tw-font-bold tw-text-gray-600 tw-uppercase tw-sticky tw-left-0 tw-bg-slate-50 tw-min-w-[200px]">Particulars</th>
                                    {months.map(m => (
                                        <th key={m.key} className="tw-px-3 tw-py-2.5 tw-text-right tw-text-[10px] tw-font-bold tw-text-gray-600 tw-uppercase tw-whitespace-nowrap">{m.label}</th>
                                    ))}
                                    <th className="tw-px-3 tw-py-2.5 tw-text-right tw-text-[10px] tw-font-bold tw-text-gray-600 tw-uppercase">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sideRows('Income', data.income, 'tw-bg-emerald-50/60 tw-text-emerald-800')}
                                {sideRows('Expenditure', data.expenditure, 'tw-bg-rose-50/60 tw-text-rose-800')}
                                <tr className="tw-border-t-2 tw-border-gray-300 tw-bg-white">
                                    <td className="tw-px-3 tw-py-2 tw-font-bold tw-text-gray-800 tw-sticky tw-left-0 tw-bg-white">Surplus / (Deficit)</td>
                                    {months.map(m => <Amount key={m.key} value={data.surplus[m.key]} bold signed />)}
                                    <Amount value={data.totals.surplus} bold signed />
                                </tr>
                                <tr className="tw-bg-gray-50">
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600 tw-sticky tw-left-0 tw-bg-gray-50">Cumulative</td>
                                    {months.map(m => <Amount key={m.key} value={data.cumulative[m.key]} signed />)}
                                    <td />
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </ReportShell>
    );
};

const FragmentGroup = ({ group, months, showAccounts }) => (
    <>
        <tr className="tw-border-t tw-border-gray-100">
            <td className="tw-px-3 tw-py-1.5 tw-font-semibold tw-text-gray-800 tw-sticky tw-left-0 tw-bg-white">{group.name}</td>
            {months.map(m => <Amount key={m.key} value={group.amounts[m.key]} bold />)}
            <Amount value={group.total} bold />
        </tr>
        {showAccounts && group.accounts.map(a => (
            <tr key={a.code} className="hover:tw-bg-gray-50/60">
                <td className="tw-pl-7 tw-pr-3 tw-py-1 tw-text-gray-600 tw-sticky tw-left-0 tw-bg-white">
                    {a.name} <span className="tw-text-[10px] tw-text-gray-400">{a.code}</span>
                </td>
                {months.map(m => <Amount key={m.key} value={a.amounts[m.key]} />)}
                <Amount value={a.total} />
            </tr>
        ))}
    </>
);

export default MonthlyIncomeExpenditure;
